// CTV-457: client-side mirror of the anniversary algorithm in
// selfservice-api/src/shared/monthly-installments.ts. The wizard uses this to
// draw vertical guide lines on the scheduler chart at every hypothetical
// monthly-flight start, before the user has committed to a payment plan
// (which happens later in step 5). Keep this in sync with the server file —
// the algorithm has to produce the same anniversary boundaries either way.

function parseDateOnly(value: string): Date | null {
  const text = String(value).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

function anniversary(start: Date, monthsToAdd: number): Date {
  const startMonth = start.getUTCMonth();
  const targetMonthIndex = startMonth + monthsToAdd;
  const year = start.getUTCFullYear() + Math.floor(targetMonthIndex / 12);
  const month = ((targetMonthIndex % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(start.getUTCDate(), lastDay)));
}

function formatDateOnly(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Period-start YYYY-MM-DD strings for the monthly-flight breakdown of the
 * given campaign window. Always includes `startDate` as the first entry.
 *
 * Mirrors `buildMonthlyInstallmentSchedule()` on the server: each subsequent
 * entry is the start day-of-month + N months, clamped to that month's last
 * day (e.g., Jan 31 + 1 month → Feb 28/29).
 *
 * Returns `[]` for invalid inputs (missing strings, end before start, etc.).
 * The chart draws an additional final tick at `endDate` separately — this
 * function only returns the period-start anniversaries.
 */
export function monthlyPeriodStarts(startDate: string, endDate: string): string[] {
  if (!startDate || !endDate) return [];
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (!start || !end || end < start) return [];

  const starts: string[] = [];
  for (let n = 0; n < 240; n++) {
    const ps = anniversary(start, n);
    if (ps > end) break;
    starts.push(formatDateOnly(ps));
  }
  return starts;
}
